import {
  Text,
  Box,
  HStack,
  VStack,
  Flex,
  Divider,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useToast,
  Button,
  ListItem,
  UnorderedList,
  Spinner,
  IconButton,
} from "@chakra-ui/react";

import { useEffect, useState } from "react";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaFileExcel,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { BalanceSolicitationAnalysisResponse } from "@/models/consignado/balanceSolicitation.model";
import { truncateTextByWords } from "@/utils/truncateText";
import { AnaliseCreditoPasso as AnaliseCreditoPassoType, ReportRelatorio } from "@/models/consignado/report-data.model";

// tipo que a API devolve para os relatórios RISK
const TIPO_RELATORIO_RISCO = "Risco";

type RelatorioVinculo =BalanceSolicitationAnalysisResponse["vinculos_empregaticios"][number]["relatorios"][number];
import { updateStatusConsignadoBalanceSolicitation } from "@/services/consignado/balanceSolicitation.service";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import {
  AnaliseCreditoBlocoCard,
  AnaliseCreditoCabecalho,
  AnaliseCreditoItem,
  AnaliseCreditoResultado,
  NOME_MOTOR_1,
  NOME_MOTOR_BMP,
  comPassosNaoAplicados,
  dataHoraDosPassos,
  passosDoMotor,
  passosDoMotorOriginacao,
  semVazios,
  visualDoBloco,
} from "@/components/consignado/AnaliseCreditoPasso";
interface MarginEmployerSelectProps {
  analysisData: BalanceSolicitationAnalysisResponse;
  setSelectedVinculoId: (vinculoId: string) => void;
  selectedVinculoId: string | null;
  balanceSolicitationId?: string;
  onStatusUpdate?: () => void;
}
export function MarginEmployerSelectReportsAnalysis({
  analysisData,
  setSelectedVinculoId,
  selectedVinculoId,
  balanceSolicitationId,
  onStatusUpdate,
}: MarginEmployerSelectProps) {
  const toast = useToast();
  const { user } = useEnsureAuthenticated();
  const listUsersApprovedToButtonsStatus = ['33773872852.1', '27168384880.1', '42513825012.1']
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false)
  const [downloadExcel, setDownloadExcel] = useState<boolean>(false)

  useEffect(() => {
    if (analysisData?.vinculos_empregaticios?.length) {
      setSelectedVinculoId(analysisData.vinculos_empregaticios[0].id);
    }
  }, [analysisData]);

  const vinculoSelecionado = analysisData?.vinculos_empregaticios?.find(
    (v) => v.id === selectedVinculoId
  );
  // analise_credito com os passos que não rodam (erro anterior) como "Não aplicado".
  const selectedVinculo = vinculoSelecionado && {
    ...vinculoSelecionado,
    analise_credito: comPassosNaoAplicados(vinculoSelecionado.analise_credito),
  };

  const formatDateTime = (date: Date | string | null) => {
    if (!date) return "Não informado";
    try {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return format(dateObj, "dd/MM/yyyy, HH:mm");
    } catch {
      return "Data inválida";
    }
  };

  const handeHasWhitelistedId = (reports: ReportRelatorio[]): boolean => {
    for (const report of reports) {
      if (report.cnpj_pre_aprovado_id) {
        return true
      }
    }
    return false
  }
  const handleApproveStatus = async () => {
    if (!balanceSolicitationId) {
      toast({
        title: "Erro",
        description: "ID da solicitação não encontrado",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await updateStatusConsignadoBalanceSolicitation(
        balanceSolicitationId,
        "SUCCESS"
      );
      toast({
        title: "Sucesso",
        description: "Solicitação aprovada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onStatusUpdate?.();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao aprovar a solicitação",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDownload = () => {
    setDownloadExcel(true);

    try {
      if (!selectedVinculo?.relatorios || selectedVinculo.relatorios.length === 0) {
        toast({
          title: "Nenhum relatório encontrado para exportar.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        setDownloadExcel(false);
        return;
      }

      const workbook = XLSX.utils.book_new();

      selectedVinculo.relatorios.forEach((report) => {
        const nomeAba = report.nome_motor?.trim() || "Relatório";
        const safeSheetName = nomeAba
          .replace(/[*?\[\]/\\:]/g, "_")
          .substring(0, 31);

        const dadosCriterios = report.criterios.map((criterio) => ({
          "CRITÉRIO": criterio.nome_criterio,
          "APROVADO": criterio.aprovado === true ? "Sim" : criterio.aprovado === false ? "Não" : "Indefinido",
          "MENSAGEM": criterio.mensagem || "-",
          "VALOR ORIGINAL": criterio.valor_original ?? "-",
        }));

        const worksheet = XLSX.utils.json_to_sheet(dadosCriterios, {
          origin: report?.motivo_rejeicao ? "A3" : 'A1',
        } as any);

        if (report.motivo_rejeicao) {
          const textoMotivo = `MOTIVO DA REJEIÇÃO: ${report.motivo_rejeicao}`;

          XLSX.utils.sheet_add_aoa(worksheet, [[textoMotivo]], { origin: "A1" });

          if (!worksheet["!merges"]) worksheet["!merges"] = [];
          worksheet["!merges"].push({
            s: { r: 0, c: 0 },
            e: { r: 0, c: 3 },
          });
        }

        worksheet["!cols"] = [
          { wch: 40 },
          { wch: 15 },
          { wch: 60 },
          { wch: 25 },
        ];

        XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
      });

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const dataBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "analise_de_credito.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Arquivo Excel da análise de crédito baixado com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onStatusUpdate?.();
    } catch (error) {
      console.error("Erro ao gerar Excel:", error);
      toast({
        title: "Erro ao gerar o arquivo Excel.",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadExcel(false);
    }
  };

  const handleRejectStatus = async () => {
    if (!balanceSolicitationId) {
      toast({
        title: "Erro",
        description: "ID da solicitação não encontrado",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await updateStatusConsignadoBalanceSolicitation(
        balanceSolicitationId,
        "UNEXPECTED_ERROR"
      );
      toast({
        title: "Sucesso",
        description: "Solicitação recusada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onStatusUpdate?.();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao recusar a solicitação",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Card de um relatório de motor (critérios de análise + resultado da
  // analise_credito daquele motor).
  const renderRelatorio = (relatorio: RelatorioVinculo) => (
    <Accordion
      key={relatorio.id}
      allowToggle
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
    >
      <AccordionItem border="none">
        <AccordionButton
          p={3}
          _hover={{ bg: "gray.50" }}
          borderRadius="md"
        >
          <Flex
            flex={1}
            align="center"
            justify="space-between"
            w="100%"
          >
            <HStack spacing={2} w="100%">
              <Box
                bg={relatorio.aprovado ? "green.500" : "red.500"}
                borderRadius="full"
                p={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  as={
                    relatorio.aprovado
                      ? FaCheckCircle
                      : FaTimesCircle
                  }
                  color="white"
                  boxSize={3}
                />
              </Box>
              <VStack align="start" spacing={0} w="100%" pr={4} textAlign="left">
                <HStack
                  spacing={1}
                  w="100%"
                  justifyContent="space-between"
                >
                  {relatorio.nome_motor && (
                    <HStack spacing={1}>
                      <Text fontSize="xs" color="gray.600">
                        <Text as="span" fontWeight="bold">
                          Nome Motor:
                        </Text>{" "}
                        {relatorio.nome_motor}
                      </Text>
                    </HStack>
                  )}

                  {relatorio.data_criacao && (
                    <HStack spacing={1}>
                      <Icon
                        as={FaCalendarAlt}
                        color="gray.500"
                        boxSize={2.5}
                      />
                      <Text fontSize="xs" color="gray.600">
                        {formatDateTime(relatorio.data_criacao)}
                      </Text>
                    </HStack>
                  )}
                </HStack>

                {relatorio.descricao_motor && (
                  <Text fontSize="xs" color="gray.600">
                    <Text as="span" fontWeight="bold">
                      Descrição Motor:
                    </Text>{" "}
                    {truncateTextByWords(
                      relatorio.descricao_motor,
                      80
                    )}
                  </Text>
                )}

                {relatorio.motivo_rejeicao && (
                  <HStack spacing={1} w="100%" align="flex-start">
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      wordBreak="break-word"
                      overflowWrap="break-word"
                      maxW="100%"
                    >
                      <Text as="span" fontWeight="bold">
                        Motivo de Recusa:
                      </Text>{" "}
                      {truncateTextByWords(
                        relatorio.motivo_rejeicao,
                        80
                      )}
                    </Text>
                  </HStack>
                )}

                {relatorio.pontuacao && (
                  <HStack spacing={1}>
                    <Text fontSize="xs" color="gray.600">
                      <Text as="span" fontWeight="bold">
                        Score:
                      </Text>{" "}
                      {relatorio.pontuacao}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </HStack>
            <AccordionIcon />
          </Flex>
        </AccordionButton>

        <AccordionPanel pb={3} px={3}>
          <Divider mb={3} />
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="gray.700"
            mb={2}
          >
            CRITÉRIOS DE ANÁLISE
          </Text>
          <VStack align="stretch" spacing={2}>
            {relatorio.criterios?.map((criterio) => (
              <Box
                key={criterio.id_criterio}
                p={2}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <HStack spacing={2} mb={1}>
                  <Icon
                    as={
                      criterio.aprovado !== false
                        ? FaCheckCircle
                        : FaTimesCircle
                    }
                    color={
                      criterio.aprovado !== false
                        ? "green.500"
                        : "red.500"
                    }
                    boxSize={3}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.700"
                  >
                    {criterio.nome_criterio}
                  </Text>

                  {criterio.mensagem && (
                    <>
                      <Text fontSize="xs" color="gray.600">
                        {" - "}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {criterio.mensagem}
                      </Text>
                    </>
                  )}
                </HStack>
                {relatorio?.nome_motor?.includes('VAAS') && criterio.valor_original !== null &&
                  criterio.valor_original !== undefined && (
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                      Valor original:{" "}
                      {String(criterio.valor_original)}
                    </Text>
                  )}
              </Box>
            ))}
          </VStack>

          {(() => {
            const passos = passosDoMotor(selectedVinculo?.analise_credito, relatorio.nome_motor);
            return passos.length > 0 && <AnaliseCreditoResultado passos={passos} mt={4} />;
          })()}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );

  // Na ordem do objeto validacoes_cnpj_cpf: o passo de cada motor aparece como
  // o card do motor (quando há relatório dele); os demais, como itens.
  const validacoesCnpjCpf = selectedVinculo?.analise_credito?.validacoes_cnpj_cpf;
  const relatoriosDoMotor = (nomeMotor: string) =>
    selectedVinculo?.relatorios?.filter((relatorio) => relatorio.nome_motor === nomeMotor) ?? [];
  const renderPassoOuMotor = (passo: AnaliseCreditoPassoType | undefined, nomeMotor?: string) => {
    const relatorios = nomeMotor ? relatoriosDoMotor(nomeMotor) : [];
    if (relatorios.length > 0) return relatorios.map(renderRelatorio);
    return passo && <AnaliseCreditoItem passo={passo} />;
  };
  const motoresNasValidacoes = validacoesCnpjCpf ? [NOME_MOTOR_1, NOME_MOTOR_BMP] : [];
  // Os relatórios de risco ("Enquadramento de Risco: AA" etc.) saem do mesmo
  // enquadramento que gera o passo enquadramento_risco: ficam dentro do card
  // de enquadramento.
  const enquadramento = selectedVinculo?.analise_credito?.enquadramento;
  const ehRelatorioDeRisco = (relatorio: RelatorioVinculo) => relatorio.tipo === TIPO_RELATORIO_RISCO;
  const relatoriosDeRisco = enquadramento ? selectedVinculo?.relatorios?.filter(ehRelatorioDeRisco) ?? [] : [];
  // Círculo do card de enquadramento: segue os passos da analise_credito; sem
  // ela, os produtos da qualificação de risco.
  const visualEnquadramento = visualDoBloco(
    enquadramento
      ? semVazios(Object.values(enquadramento))
      : [{ status: (selectedVinculo?.qualificacao_risco?.[0]?.produtos?.length ?? 0) > 0 ? "sucesso" : "erro" }]
  );
  const relatoriosForaDasValidacoes =
    selectedVinculo?.relatorios?.filter(
      (relatorio) =>
        !motoresNasValidacoes.includes(relatorio.nome_motor ?? "") &&
        !(enquadramento && ehRelatorioDeRisco(relatorio))
    ) ?? [];

  return (
    <Box
      flex={{ base: "none", lg: "1" }}
      bg="white"
      borderRadius="lg"
      p={3}
      boxShadow="sm"
      maxH={{ base: "500px", lg: "none" }}
      border="1px solid"
      borderColor="gray.200"
      css={{
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      minH={0}
    >
      <Flex
        alignItems='center'
        justifyContent='space-between'
        gap={4}
        mb={0}
      >
        <Flex
          alignItems='center'
          gap={1}
        >
          <Icon as={FaFileAlt} color="secondary" />
          <Text fontSize="md" fontWeight="bold" color="gray.700">
            Relatórios de Análise -
          </Text>

          <Text fontSize="md" fontWeight="bold" color="gray.700" lineHeight='1.2rem'>
            {selectedVinculo?.empregador.nome || "Empregador não informado"}
          </Text>
        </Flex>

        {!selectedVinculo?.motivo_rejeicao && (
          downloadExcel ? <Spinner
            size="sm"
            color="primary"
            thickness="4px"
            speed="0.65s"
          /> : <IconButton
            icon={<FaFileExcel />}
            size='sm'
            aria-label="Download Excel"
            onClick={handleDownload}
            colorScheme="green"
          />
        )}
      </Flex>

      {selectedVinculo ? (
        <>
          {!selectedVinculo.motivo_rejeicao && (
            <Flex ml={5} gap={1} mt='-0.1rem' alignItems='center'>
              <Text fontSize='0.8rem' fontFamily='Lexend Deca' fontWeight='500'>
                Vencimentos: {" "}
                {selectedVinculo?.total_vencimentos && (
                  <Text as={'span'} fontWeight='400'>
                    {selectedVinculo?.total_vencimentos?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </Text>
                )}
              </Text>
         
              <Text fontSize='0.8rem' fontFamily='Lexend Deca' fontWeight='500'>
                Margem: {" "}
                {selectedVinculo?.margem && (
                  <Text as={'span'} fontWeight='400'>
                    {selectedVinculo?.margem?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </Text>
                )}
              </Text>

              <Text fontSize='0.8rem' fontFamily='Lexend Deca' fontWeight='500'>
                - Margem Disponível: {" "}
                {selectedVinculo?.margem_disponivel && (
                  <Text as={'span'} fontWeight='400'>
                    {selectedVinculo?.margem_disponivel?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </Text>
                )}
              </Text>

              <Text fontSize='0.8rem' fontFamily='Lexend Deca' fontWeight='500'>
                - Dívidas: {" "}
                {selectedVinculo?.total_dividas_cpf && (
                  <Text as={'span'} fontWeight='400'>
                    {selectedVinculo?.total_dividas_cpf?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </Text>
                )}
              </Text>

            </Flex>
          )}

          {Number(selectedVinculo?.qtd_emprestimos_ativos_suspensos ?? 0) > 0 && (
            <Flex ml={5} gap={1} mt='-0.1rem' alignItems='center'>
              <Text fontSize='0.8rem' fontFamily='Lexend Deca' fontWeight='500'>
                Quantidade de empréstimos ativos suspensos: {" "}
                {selectedVinculo?.qtd_emprestimos_ativos_suspensos}
              </Text>
            </Flex>
          )}

          {handeHasWhitelistedId(selectedVinculo?.relatorios) && (
            <Text ml={5} mb={-2} fontSize='0.6rem' fontFamily='Lexend Deca' color='green.600' textAlign="left">
              Este CNPJ consta na lista de pré-aprovados.
            </Text>
          )}

          {selectedVinculo.motivo_rejeicao && (
            <Box flexShrink={0} mt={4}>
              <HStack spacing={1}>
                <Text fontSize="medium" textAlign="center" color="gray.600" p={2} borderRadius="md" width="100%">
                  <Text as="span" fontWeight="bold">
                    Motivo de Recusa:
                  </Text>{" "}
                  {selectedVinculo.motivo_rejeicao}
                </Text>
              </HStack>
            </Box>
          )}

          {!selectedVinculo.motivo_rejeicao && selectedVinculo.relatorios?.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={8}>
              Nenhum relatório disponível para este vínculo
            </Text>
          ) : (
            <Box
              flex={1}
              overflowY="auto"
              mt={4}
              minH={0}
              css={{
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <VStack align="stretch" spacing={2}>
                {validacoesCnpjCpf && (
                  <AnaliseCreditoBlocoCard
                    titulo="Validações de CNPJ e CPF"
                    descricao="valida empregador e trabalhador: critérios mínimos, aprovação do CNPJ, quantidade de funcionários, margem na bancarizadora e critérios totais"
                    passos={semVazios(Object.values(validacoesCnpjCpf))}
                  >
                    <VStack align="stretch" spacing={2}>
                      {renderPassoOuMotor(validacoesCnpjCpf.criterios_dados_minimos, NOME_MOTOR_1)}
                      {renderPassoOuMotor(validacoesCnpjCpf.aprovacao_pj)}
                      {renderPassoOuMotor(validacoesCnpjCpf.quantidade_funcionarios)}
                      {renderPassoOuMotor(validacoesCnpjCpf.consulta_margem)}
                      {renderPassoOuMotor(validacoesCnpjCpf.criterios_dados_totais, NOME_MOTOR_BMP)}
                    </VStack>
                  </AnaliseCreditoBlocoCard>
                )}

                {relatoriosForaDasValidacoes.map(renderRelatorio)}

                {selectedVinculo?.analise_credito?.motor_originacao && (
                  <AnaliseCreditoBlocoCard
                    titulo="Motor de Originação"
                    descricao="analisa o score, a dívida de FGTS do CNPJ e a situação do CPF e do CNPJ na Receita"
                    passos={passosDoMotorOriginacao(selectedVinculo.analise_credito.motor_originacao)}
                    rotuloDescricaoMotor
                  />
                )}

                {(selectedVinculo?.qualificacao_risco?.length > 0 || selectedVinculo?.analise_credito?.enquadramento) && (
                  <Accordion
                    allowToggle
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                  >
                    <AccordionItem border="none">
                      <AccordionButton
                        p={3}
                        _hover={{ bg: "gray.50" }}
                        borderRadius="md"
                      >
                        <Flex
                          flex={1}
                          align="center"
                          justify="space-between"
                          w="100%"
                        >
                          <HStack spacing={2} w="100%">
                            <Box
                              bg={visualEnquadramento.cor}
                              borderRadius="full"
                              p={1.5}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon as={visualEnquadramento.icone} color="white" boxSize={3} />
                            </Box>
                            <AnaliseCreditoCabecalho
                              nome="Enquadramento de Risco e Produtos"
                              descricao="Apresenta a classificação de risco e os produtos consignados elegíveis."
                              rotuloDescricaoMotor
                              dataHora={dataHoraDosPassos(
                                semVazios(Object.values(selectedVinculo?.analise_credito?.enquadramento ?? {}))
                              )}
                            />
                          </HStack>
                          <AccordionIcon />
                        </Flex>
                      </AccordionButton>

                      <AccordionPanel pb={3} px={3}>
                        <Divider mb={3} />
                        {relatoriosDeRisco.length > 0 && (
                          <VStack align="stretch" spacing={2} mb={2}>
                            {relatoriosDeRisco.map(renderRelatorio)}
                          </VStack>
                        )}
                        {selectedVinculo?.analise_credito?.enquadramento && (
                          <AnaliseCreditoResultado
                            passos={semVazios([
                              selectedVinculo.analise_credito.enquadramento.enquadramento_risco,
                              selectedVinculo.analise_credito.enquadramento.produtos_validos,
                            ])}
                          />
                        )}
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color="gray.700"
                          mb={2}
                          mt={selectedVinculo?.analise_credito?.enquadramento ? 4 : 0}
                        >
                          Resultado
                        </Text>
                        <VStack align="stretch" spacing={2}>

                          {selectedVinculo?.qualificacao_risco?.map(risk => (
                            <Box
                              key={risk.id_qualificacao_risco}
                              p={2}
                              bg="gray.50"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="gray.200"
                            >
                              {risk.nome_qualificacao_risco && (
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color="gray.700"
                                  mb={1}
                                >
                                  Classificação de Risco: {" "}
                                  <Text as="span" fontWeight="400">
                                    {risk.nome_qualificacao_risco}
                                  </Text>
                                </Text>
                              )}

                              {risk?.produtos?.length > 0 && risk?.produtos.sort((a, b) => a.parcelas - b.parcelas).map(product => (
                                <UnorderedList mb={[2, 0]}>
                                  <ListItem fontSize='.5rem' >
                                    <Flex gap={[0, 0, 4]} alignItems='start' flexDirection={['column', 'row', 'row']}>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="gray.700"
                                        width='7rem'
                                      >
                                        Produto: {" "}
                                        <Text as="span" fontWeight="400">
                                          {product.nome}
                                        </Text>
                                      </Text>

                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="gray.700"
                                        width='5rem'
                                      >
                                        Parcelas: {" "}
                                        <Text as="span" fontWeight="400">
                                          {product.parcelas}
                                        </Text>
                                      </Text>

                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="gray.700"
                                      >
                                        Taxa Mensal: {" "}
                                        <Text as="span" fontWeight="400">
                                          {product.taxa_mensal * 100}%
                                        </Text>
                                      </Text>
                                    </Flex>
                                  </ListItem>
                                </UnorderedList>
                              ))}

                            </Box>
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}
              </VStack>
            </Box>
          )}
        </>
      ) : (
        <Text color="gray.500" textAlign="center" py={8}>
          Selecione um vínculo empregatício para ver os relatórios
        </Text>
      )}
      {selectedVinculo && listUsersApprovedToButtonsStatus.filter(u => u === user?.id).length > 0 && (
        <Flex
          gap={3}
          mt={4}
          pt={4}
          borderTop="1px solid"
          borderColor="gray.200"
          justifyContent="flex-end"
        >
          <Button
            colorScheme="red"
            variant="outline"
            onClick={handleRejectStatus}
            isLoading={isUpdatingStatus}
            isDisabled={isUpdatingStatus}
          >
            Negar
          </Button>
          <Button
            colorScheme="green"
            onClick={handleApproveStatus}
            isLoading={isUpdatingStatus}
            isDisabled={isUpdatingStatus}
          >
            Aprovar
          </Button>
        </Flex>
      )}
    </Box>
  );
}
